import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, CalendarPlus, Home, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from "@/components/ui/dialog";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";

const CustomerDetail = () => {
    const { id } = useParams();
    const nav = useNavigate();
    const [customer, setCustomer] = useState(null);
    const [properties, setProperties] = useState([]);
    const [loading, setLoading] = useState(true);
    const [addr, setAddr] = useState("");
    const [open, setOpen] = useState(false);
    const [saving, setSaving] = useState(false);
    // Quick job dialog state
    const [jobOpen, setJobOpen] = useState(false);
    const [jobSaving, setJobSaving] = useState(false);
    const [jobPropertyId, setJobPropertyId] = useState("");
    const [jobDate, setJobDate] = useState("");
    const [jobTime, setJobTime] = useState("");
    const [jobTBC, setJobTBC] = useState(false);
    const [jobNotes, setJobNotes] = useState("");

    const load = () =>
        Promise.all([
            api.get(`/customers/${id}`),
            api.get("/properties", { params: { customer_id: id } }),
        ])
            .then(([c, p]) => {
                setCustomer(c.data);
                setProperties(p.data);
            })
            .catch((err) => {
                toast.error(err?.response?.data?.detail || "Load failed");
                nav("/customers");
            })
            .finally(() => setLoading(false));

    useEffect(() => {
        load();
    }, [id]);

    const addProperty = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            await api.post("/properties", { customer_id: id, address: addr.trim() });
            setAddr("");
            setOpen(false);
            toast.success("Property added");
            load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Save failed");
        } finally {
            setSaving(false);
        }
    };

    const removeProperty = async (pid) => {
        if (!confirm("Delete this property and all its jobs?")) return;
        try {
            await api.delete(`/properties/${pid}`);
            toast.success("Property deleted");
            load();
        } catch {
            toast.error("Delete failed");
        }
    };

    const openJobDialog = () => {
        setJobPropertyId(properties[0]?.id || "");
        setJobDate("");
        setJobTime("");
        setJobTBC(false);
        setJobNotes("");
        setJobOpen(true);
    };

    const saveQuickJob = async (e) => {
        e.preventDefault();
        if (!jobPropertyId) {
            toast.error("Add a property first");
            return;
        }
        if (!jobDate) {
            toast.error("Pick a date");
            return;
        }
        setJobSaving(true);
        try {
            const appointment_date = jobTBC || !jobTime
                ? jobDate
                : `${jobDate}T${jobTime}:00`;
            await api.post("/jobs/quick", {
                customer_id: id,
                property_id: jobPropertyId,
                appointment_date,
                time_tbc: jobTBC || !jobTime,
                notes: jobNotes.trim() || null,
            });
            toast.success("Job added to the diary");
            setJobOpen(false);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Save failed");
        } finally {
            setJobSaving(false);
        }
    };

    if (loading) return <p className="text-label">Loading…</p>;
    if (!customer) return null;

    return (
        <div className="space-y-6" data-testid="customer-detail-page">
            <Link to="/customers" className="inline-flex items-center gap-2 text-sm font-mono" data-testid="back-to-customers">
                <ArrowLeft size={14} /> All customers
            </Link>

            <div className="dh-card p-6">
                <p className="text-label">Customer</p>
                <h2 className="font-display font-black text-3xl uppercase tracking-tight leading-none">
                    {customer.name}
                </h2>
                <div className="grid sm:grid-cols-3 gap-4 mt-4 text-sm">
                    <div>
                        <p className="text-label">Email</p>
                        <p className="font-mono">{customer.email || "—"}</p>
                    </div>
                    <div>
                        <p className="text-label">Mobile</p>
                        <p className="font-mono">{customer.mobile || "—"}</p>
                    </div>
                    <div>
                        <p className="text-label">Notes</p>
                        <p>{customer.notes || "—"}</p>
                    </div>
                </div>
            </div>

            <div className="flex items-end justify-between">
                <div>
                    <p className="text-label">Sites</p>
                    <h3 className="font-display font-black text-2xl uppercase tracking-tight leading-none">
                        Properties
                    </h3>
                </div>
                <div className="flex gap-2">
                    <button
                        className="dh-btn"
                        data-testid="new-job-btn"
                        onClick={openJobDialog}
                        disabled={properties.length === 0}
                        title={properties.length === 0 ? "Add a property first" : "Book a job"}
                    >
                        <CalendarPlus size={16} /> New job
                    </button>
                    <Dialog open={open} onOpenChange={setOpen}>
                    <DialogTrigger asChild>
                        <button className="dh-btn" data-testid="new-property-btn">
                            <Plus size={16} /> New property
                        </button>
                    </DialogTrigger>
                    <DialogContent className="bg-white border-2 border-[color:var(--dh-line)] rounded-none">
                        <DialogHeader>
                            <DialogTitle className="font-display uppercase">Add property</DialogTitle>
                        </DialogHeader>
                        <form onSubmit={addProperty} className="space-y-4" data-testid="property-form">
                            <div>
                                <label className="text-label block mb-1">Address</label>
                                <textarea
                                    required
                                    rows={3}
                                    className="dh-input"
                                    data-testid="property-address-input"
                                    value={addr}
                                    onChange={(e) => setAddr(e.target.value)}
                                />
                            </div>
                            <DialogFooter>
                                <button className="dh-btn" data-testid="property-save-btn" disabled={saving}>
                                    {saving ? "Saving…" : "Add property"}
                                </button>
                            </DialogFooter>
                        </form>
                    </DialogContent>
                </Dialog>
                </div>
            </div>

            <Dialog open={jobOpen} onOpenChange={setJobOpen}>
                <DialogContent className="bg-white border-2 border-[color:var(--dh-line)] rounded-none">
                    <DialogHeader>
                        <DialogTitle className="font-display uppercase">Book a job for {customer.name}</DialogTitle>
                    </DialogHeader>
                    <form onSubmit={saveQuickJob} className="space-y-4" data-testid="quick-job-form">
                        {properties.length > 1 && (
                            <div>
                                <label className="text-label block mb-1">Property</label>
                                <Select value={jobPropertyId} onValueChange={setJobPropertyId}>
                                    <SelectTrigger data-testid="quick-job-property">
                                        <SelectValue placeholder="Pick a property" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {properties.map((p) => (
                                            <SelectItem key={p.id} value={p.id} data-testid={`quick-job-property-${p.id}`}>
                                                {p.address}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                        )}
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <label className="text-label block mb-1">Date</label>
                                <input
                                    type="date"
                                    required
                                    className="dh-input"
                                    data-testid="quick-job-date"
                                    value={jobDate}
                                    onChange={(e) => setJobDate(e.target.value)}
                                />
                            </div>
                            <div>
                                <label className="text-label block mb-1">
                                    Time {jobTBC && <span className="text-neutral-500">(TBC)</span>}
                                </label>
                                <input
                                    type="time"
                                    className="dh-input"
                                    data-testid="quick-job-time"
                                    disabled={jobTBC}
                                    value={jobTime}
                                    onChange={(e) => setJobTime(e.target.value)}
                                />
                            </div>
                        </div>
                        <label className="flex items-center gap-2 text-sm">
                            <input
                                type="checkbox"
                                data-testid="quick-job-tbc"
                                checked={jobTBC}
                                onChange={(e) => setJobTBC(e.target.checked)}
                            />
                            <span>Time TBC — I&rsquo;ll confirm with the customer later</span>
                        </label>
                        <div>
                            <label className="text-label block mb-1">Notes (optional)</label>
                            <textarea
                                rows={3}
                                className="dh-input"
                                data-testid="quick-job-notes"
                                value={jobNotes}
                                onChange={(e) => setJobNotes(e.target.value)}
                            />
                        </div>
                        <DialogFooter>
                            <button className="dh-btn" data-testid="quick-job-save" disabled={jobSaving}>
                                {jobSaving ? "Saving…" : "Book it in"}
                            </button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>

            {properties.length === 0 ? (
                <div className="dh-card p-6 text-center">
                    <Home className="mx-auto text-neutral-400" size={32} />
                    <p className="mt-2 font-display uppercase font-bold">No properties yet</p>
                </div>
            ) : (
                <ul className="space-y-2" data-testid="property-list">
                    {properties.map((p) => (
                        <li key={p.id} className="dh-card p-4 flex gap-3 items-center" data-testid={`property-row-${p.id}`}>
                            <Link to={`/properties/${p.id}`} className="flex-1 min-w-0">
                                <p className="font-display font-black uppercase text-lg leading-tight">{p.address}</p>
                            </Link>
                            <button
                                className="dh-btn"
                                data-variant="danger"
                                data-testid={`property-delete-${p.id}`}
                                onClick={() => removeProperty(p.id)}
                            >
                                <Trash2 size={14} />
                            </button>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
};

export default CustomerDetail;
